import { isMetric } from "@/components/common/settings-context/distance-unit-utils";
import { useDistanceUnit } from "@/components/common/settings-context/settings-context";
import {
  selectIpxEstimatedRegionBytes,
  selectIpxRegionVolume,
} from "@/store/modes/ipx-order-mode-selectors";
import {
  setIpxCheckout,
  setIpxOrder,
} from "@/store/modes/ipx-order-mode-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { goToNextWizardStep } from "@/store/wizard/wizard-slice";
import {
  CloseIcon,
  FaroIconButton,
  FaroText,
  FaroTooltip,
  grey,
  useBreakpointMdUp,
} from "@faro-lotv/flat-ui";
import { Dialog, Stack } from "@mui/material";
import { useEffect, useRef, useState, type JSX } from "react";
import { IPX_EMBED_URL } from "./ipx-config";
import { isIpxEmbedMessage } from "./ipx-embed-messages";

/**
 * The checkout of IPX, on its own surface so it is clear that IPX, not FARO, takes the order and the payment.
 * In production this could be IPX's page in a new tab: the wizard only waits for the confirmed order.
 *
 * @returns the IPX checkout while it's open
 */
export function IpxCheckoutDialog(): JSX.Element {
  const dispatch = useAppDispatch();
  const isMdUp = useBreakpointMdUp();
  const isOpen = useAppSelector(
    (state) => state.ipxOrderMode.checkout === "open",
  );
  function cancel(): void {
    dispatch(setIpxCheckout("cancelled"));
  }

  return (
    <Dialog
      open={isOpen}
      onClose={cancel}
      maxWidth="md"
      fullWidth
      // A whole screen for the IPX checkout on phones
      fullScreen={!isMdUp}
      aria-labelledby="ipx-checkout-title"
      slotProps={{
        paper: { sx: isMdUp ? { height: "min(880px, 90dvh)" } : undefined },
      }}
    >
      <Stack
        component="header"
        direction="row"
        sx={{
          alignItems: "center",
          gap: 2,
          px: 3,
          py: 1.5,
          borderBottom: `1px solid ${grey[200]}`,
        }}
      >
        <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
          <FaroText id="ipx-checkout-title" variant="heading16">
            Integrated Projects (IPX) checkout
          </FaroText>
          <FaroText variant="bodyS" color={grey[600]}>
            IPX takes your order and payment. FARO doesn&apos;t see or store
            your payment details.
          </FaroText>
        </Stack>
        <FaroTooltip title="Close the checkout, nothing is charged">
          <FaroIconButton aria-label="close IPX checkout" onClick={cancel}>
            <CloseIcon />
          </FaroIconButton>
        </FaroTooltip>
      </Stack>
      <IpxCheckoutFrame />
    </Dialog>
  );
}

/** @returns the checkout page of IPX, moving the wizard on once the order is placed */
function IpxCheckoutFrame(): JSX.Element {
  const dispatch = useAppDispatch();
  const frameRef = useRef<HTMLIFrameElement>(null);
  const sizeBytes = useAppSelector(selectIpxEstimatedRegionBytes) ?? 0;
  const volume = useAppSelector(selectIpxRegionVolume) ?? 0;
  const distanceUnit = useDistanceUnit();

  // Computed once so the page doesn't reload while the user is ordering.
  // Only the region size is shared: no personal or project data reaches IPX through the page.
  const [src] = useState(
    () =>
      `${IPX_EMBED_URL}?${new URLSearchParams({
        sizeBytes: `${sizeBytes}`,
        volume: `${Math.round(volume)}`,
        units: isMetric(distanceUnit) ? "metric" : "imperial",
      })}`,
  );

  useEffect(() => {
    function onMessage(event: MessageEvent): void {
      // The sandboxed frame has an opaque origin, so trust is based on the sending window
      if (event.source !== frameRef.current?.contentWindow) return;
      if (!isIpxEmbedMessage(event.data)) return;

      if (event.data.type === "orderConfirmed") {
        dispatch(setIpxOrder(event.data.order));
        dispatch(setIpxCheckout("closed"));
        dispatch(goToNextWizardStep());
      } else {
        dispatch(setIpxCheckout("cancelled"));
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [dispatch]);

  return (
    <iframe
      ref={frameRef}
      src={src}
      title="Integrated Projects checkout"
      // Popups for the IPX terms, opened from the checkout
      sandbox="allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
      referrerPolicy="no-referrer"
      style={{ flexGrow: 1, width: "100%", border: 0 }}
    />
  );
}
